import {
  Avatar,
  Box,
  Grid,
  LinearProgress,
  Paper,
  Tooltip,
} from "@mui/material";
import { JSX, useState } from "react";
import {
  ArrowDropDown,
  ArrowDropUp,
  FileDownloadOffOutlined,
  UploadFile,
} from "@mui/icons-material";
import { Link, paletteVar, Typography } from "@platform/design-system";

import { DOCUMENT_NAMES } from "../constants/rfp";

interface Props {
  file: File;
  loading?: string;
  error?: {
    id: string;
    msg: string;
  };
  maxDisplayable?: number;
  onFileDownload?: VoidFunction;
}

const FileCard = ({
  file,
  loading,
  error,
  maxDisplayable = 2,
  onFileDownload = () => {},
}: Props): JSX.Element => {
  const productByTypeKeys = Object.keys(file.productsByType).sort();
  const canBeExpanded = productByTypeKeys.length > maxDisplayable;
  const [expanded, setExpanded] = useState(false);
  const hasError = !!error && error.id === file.id;
  const keysToDisplay = expanded
    ? productByTypeKeys
    : productByTypeKeys.slice(0, maxDisplayable);

  const fileKey = (productTypeKey: string) => {
    const products = file.productsByType[productTypeKey].join(", ");
    return (
      <Box key={productTypeKey} display="flex" gap={1}>
        <Typography
          variant="body-medium"
          color="textSecondary"
          sx={{ whiteSpace: "nowrap" }}
        >
          <strong>
            {Object.values(DOCUMENT_NAMES).includes(productTypeKey)
              ? productTypeKey
              : "Other"}
            {` • `}
          </strong>
        </Typography>
        <Tooltip
          sx={{ fontSize: 17, marginLeft: 1 }}
          title={products}
          followCursor
        >
          <Typography
            variant="body-medium"
            color="textSecondary"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <strong>{products}</strong>
          </Typography>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 0, sm: 2 }}>
          <Avatar
            sx={{
              bgcolor: file.infected
                ? paletteVar("surfaces-high", "#fdeded")
                : paletteVar("surfaces-secondary", "#FAF0FA"),
            }}
          >
            {file.infected ? (
              <FileDownloadOffOutlined color="error" />
            ) : (
              <UploadFile color="brandPrimary" />
            )}
          </Avatar>
        </Grid>
        <Grid size={{ xs: 12, sm: 10 }}>
          <Box
            sx={{
              cursor: file.infected ? "default" : "pointer",
            }}
          >
            {hasError ? (
              <Typography
                color="danger"
                sx={{ wordWrap: "break-word" }}
                onClick={onFileDownload}
              >
                <strong>{file.original.name}</strong>
              </Typography>
            ) : (
              <Link
                component="button"
                underline="none"
                onClick={onFileDownload}
                sx={{ wordWrap: "break-word", textAlign: "left" }}
              >
                <strong>{file.original.name}</strong>
              </Link>
            )}
            <Box>
              {hasError ? (
                <Typography variant="body-medium" color="danger" ml={1}>
                  <strong>{error.msg}</strong>
                </Typography>
              ) : (
                <Box overflow="hidden">
                  {(!loading || loading !== file.id) && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      maxWidth="60%"
                    >
                      {file.additionalDoc && (
                        <Box display="flex" gap={1}>
                          <Typography
                            variant="body-medium"
                            color="textSecondary"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            <strong>Additional Document</strong>
                          </Typography>
                        </Box>
                      )}
                      {keysToDisplay.map((key) => fileKey(key))}

                      {canBeExpanded && (
                        <Grid
                          size={6}
                          sx={{
                            display: "flex",
                            cursor: "pointer",
                            alignItems: "center",
                          }}
                          onClick={() => setExpanded(!expanded)}
                        >
                          <Avatar sx={{ background: "transparent" }}>
                            {expanded ? (
                              <ArrowDropUp color="brandPrimary" />
                            ) : (
                              <ArrowDropDown color="brandPrimary" />
                            )}
                          </Avatar>
                          <Link
                            component="span"
                            underline="none"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            <Typography
                              variant="body-medium"
                              component="span"
                              color="inherit"
                            >
                              <strong>
                                {expanded ? "VIEW LESS" : "VIEW MORE"}
                              </strong>
                            </Typography>
                          </Link>
                        </Grid>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            {loading === file.id && (
              <LinearProgress
                sx={{ width: "100%", marginTop: 1 }}
                color="brandPrimary"
              />
            )}
            {file.infected ? (
              <Tooltip
                sx={{ fontSize: 17, marginLeft: 1 }}
                title="A virus was found on the original document. Please send a non-corrupted version to your analyst."
              >
                <Typography
                  variant="body-medium"
                  width="fit-content"
                  className="with-underline"
                  color="danger"
                >
                  Corrupted file
                </Typography>
              </Tooltip>
            ) : null}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

interface File {
  id: string;
  original: {
    name: string;
  };
  productsByType: Record<string, string[]>;
  infected?: boolean;
  additionalDoc?: boolean;
}

export default FileCard;
